import React, { useState, useEffect, useCallback } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiImage,
  FiToggleLeft,
  FiToggleRight,
  FiLoader,
  FiTag,
  FiArrowUp,
  FiArrowDown,
  FiEye,
  FiEyeOff,
  FiGrid,
  FiList,
  FiChevronRight,
  FiChevronDown,
  FiFolder,
  FiSettings,
  FiLink,
  FiMove,
} from "react-icons/fi";
import { MdCategory, MdDashboard, MdMenuBook } from "react-icons/md";
import "../../styles/category.css"

// ── Styles ─────────────────────────────────────────────────────────────
const C = {
  bg: "#f8fafc",
  surface: "#ffffff",
  card: "#ffffff",
  border: "rgba(0,0,0,0.08)",
  primary: "#8b5cf6",
  primaryLight: "#a78bfa",
  primaryDark: "#7c3aed",
  primaryGradient: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
  green: "#10b981",
  greenLight: "#34d399",
  red: "#ef4444",
  redLight: "#f87171",
  orange: "#f59e0b",
  blue: "#3b82f6",
  text: "#1e293b",
  textLight: "#64748b",
  muted: "#94a3b8",
  white: "#ffffff",
  shadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  shadowLg:
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
};

const S = {
  container: {
    padding: "20px 24px",
    minHeight: "100vh",
    background: C.bg,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 28,
    flexWrap: "wrap",
    gap: 16,
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  titleIcon: {
    width: 48,
    height: 48,
    background: C.primaryGradient,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    color: C.white,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: C.text,
    letterSpacing: "-0.02em",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: C.textLight,
  },
  stats: {
    display: "flex",
    gap: 12,
  },
  statBadge: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 16px",
    background: C.surface,
    borderRadius: 40,
    fontSize: 13,
    fontWeight: 500,
    color: C.text,
    border: `1px solid ${C.border}`,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 28,
    flexWrap: "wrap",
  },
  searchWrap: {
    position: "relative",
    flex: 1,
    minWidth: 260,
    maxWidth: 380,
  },
  searchIcon: {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    color: C.textLight,
    fontSize: 16,
  },
  searchInput: {
    width: "100%",
    padding: "10px 16px 10px 44px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    color: C.text,
    fontSize: 13,
    outline: "none",
    transition: "all 0.2s",
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    background: C.primaryGradient,
    border: "none",
    borderRadius: 12,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "transform 0.15s, box-shadow 0.15s",
  },
  viewToggle: {
    display: "flex",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    overflow: "hidden",
  },
  viewBtn: (active) => ({
    padding: "8px 12px",
    background: active ? C.primary : "transparent",
    color: active ? C.white : C.textLight,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    transition: "all 0.2s",
  }),
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 20,
    marginBottom: 24,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    marginBottom: 24,
  },
  card: {
    background: C.surface,
    borderRadius: 16,
    overflow: "hidden",
    transition: "all 0.25s ease",
    border: `1px solid ${C.border}`,
    position: "relative",
  },
  cardImage: {
    width: "100%",
    height: 160,
    objectFit: "cover",
    background: "#f1f5f9",
  },
  cardImagePlaceholder: {
    width: "100%",
    height: 160,
    background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: C.textLight,
    fontSize: 40,
  },
  cardBody: {
    padding: "16px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  cardName: {
    fontSize: 18,
    fontWeight: 700,
    color: C.text,
    margin: 0,
  },
  cardSlug: {
    fontSize: 11,
    color: C.textLight,
    fontFamily: "monospace",
    background: "#f1f5f9",
    padding: "2px 8px",
    borderRadius: 4,
    display: "inline-block",
    marginBottom: 8,
  },
  cardDesc: {
    fontSize: 13,
    color: C.textLight,
    margin: "10px 0",
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  cardMeta: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 12,
    paddingTop: 12,
    borderTop: `1px solid ${C.border}`,
  },
  badge: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: active ? "rgba(16, 185, 129, 0.1)" : "rgba(239, 68, 68, 0.1)",
    color: active ? C.green : C.red,
  }),
  megaBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    background: "rgba(139, 92, 246, 0.1)",
    color: C.primary,
  },
  orderBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11,
    background: "#f1f5f9",
    color: C.textLight,
  },
  actionBtns: {
    display: "flex",
    gap: 8,
  },
  iconBtn: (color) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    background: C.surface,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: 14,
    color: color || C.textLight,
    transition: "all 0.2s",
  }),
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    color: C.textLight,
  },
  loadingState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "80px 0",
    color: C.primary,
  },
};

// ── API helpers ───────────────────────────────────────────────────────────
const BASE = "https://lightslategrey-mole-360881.hostingersite.com/api";

async function catFetch(endpoint, options = {}) {
  const token = localStorage.getItem("kkb_token");
  const cfg = {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    ...options,
  };

  if (
    cfg.body &&
    typeof cfg.body === "object" &&
    !(cfg.body instanceof FormData)
  ) {
    cfg.body = JSON.stringify(cfg.body);
  }

  const res = await fetch(`${BASE}/admin/categories${endpoint}`, cfg);
  let data = {};
  const contentType = res.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    data = await res.json();
  }
  if (!res.ok) {
    throw new Error(
      data.message || data.error || `Request failed with status ${res.status}`,
    );
  }
  return data;
}

const catGet = () => catFetch("");
const catPost = (body) => catFetch("", { method: "POST", body });
const catPut = (id, body) => catFetch(`/${id}`, { method: "PUT", body });
const catDelete = (id) => catFetch(`/${id}`, { method: "DELETE" });
const reorderCategories = (orders) =>
  catFetch("/reorder", { method: "POST", body: { orders } });

// ── Mega Menu Editor Component ──────────────────────────────────────────────
function MegaMenuEditor({ value, onChange }) {
  const [columns, setColumns] = useState(value || []);

  const addColumn = () => {
    const updated = [...columns, { title: "", links: [] }];
    setColumns(updated);
    onChange(updated);
  };

  const updateColumn = (idx, field, val) => {
    const updated = [...columns];
    updated[idx][field] = val;
    setColumns(updated);
    onChange(updated);
  };

  const addLink = (colIdx) => {
    const updated = [...columns];
    updated[colIdx].links.push({ label: "", slug: "", image: "" });
    setColumns(updated);
    onChange(updated);
  };

  const updateLink = (colIdx, linkIdx, field, val) => {
    const updated = [...columns];
    updated[colIdx].links[linkIdx][field] = val;
    setColumns(updated);
    onChange(updated);
  };

  const removeLink = (colIdx, linkIdx) => {
    const updated = [...columns];
    updated[colIdx].links.splice(linkIdx, 1);
    setColumns(updated);
    onChange(updated);
  };

  const removeColumn = (colIdx) => {
    const updated = columns.filter((_, i) => i !== colIdx);
    setColumns(updated);
    onChange(updated);
  };

  const moveColumnUp = (idx) => {
    if (idx === 0) return;
    const updated = [...columns];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    setColumns(updated);
    onChange(updated);
  };

  const moveColumnDown = (idx) => {
    if (idx === columns.length - 1) return;
    const updated = [...columns];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    setColumns(updated);
    onChange(updated);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <label
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: C.textLight,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Mega Menu Columns
        </label>
        <button
          type="button"
          onClick={addColumn}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: C.primaryLight,
            border: "none",
            borderRadius: 8,
            color: C.white,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          <FiPlus size={12} /> Add Column
        </button>
      </div>

      {columns.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 40,
            background: "#f8fafc",
            borderRadius: 12,
            color: C.textLight,
          }}
        >
          <MdMenuBook size={48} style={{ opacity: 0.5, marginBottom: 12 }} />
          <p>No mega menu columns configured</p>
          <button
            onClick={addColumn}
            style={{
              marginTop: 12,
              padding: "8px 16px",
              background: C.primary,
              border: "none",
              borderRadius: 8,
              color: C.white,
              cursor: "pointer",
            }}
          >
            Create First Column
          </button>
        </div>
      )}

      {columns.map((col, colIdx) => (
        <div
          key={colIdx}
          style={{
            background: "#f8fafc",
            padding: 16,
            borderRadius: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => moveColumnUp(colIdx)}
                style={S.iconBtn(C.textLight)}
                disabled={colIdx === 0}
              >
                <FiArrowUp size={12} />
              </button>
              <button
                onClick={() => moveColumnDown(colIdx)}
                style={S.iconBtn(C.textLight)}
                disabled={colIdx === columns.length - 1}
              >
                <FiArrowDown size={12} />
              </button>
            </div>
            <button
              onClick={() => removeColumn(colIdx)}
              style={S.iconBtn(C.red)}
            >
              <FiTrash2 size={12} />
            </button>
          </div>
          <input
            style={{
              width: "100%",
              padding: "10px 12px",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 13,
            }}
            placeholder="Column Title (e.g., LUNGIES)"
            value={col.title}
            onChange={(e) => updateColumn(colIdx, "title", e.target.value)}
          />
          <div style={{ marginLeft: 12 }}>
            {col.links.map((link, linkIdx) => (
              <div
                key={linkIdx}
                style={{
                  marginBottom: 16,
                  paddingBottom: 16,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                  <input
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      fontSize: 13,
                    }}
                    placeholder="Label"
                    value={link.label}
                    onChange={(e) =>
                      updateLink(colIdx, linkIdx, "label", e.target.value)
                    }
                  />
                  <input
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      fontSize: 13,
                    }}
                    placeholder="Slug"
                    value={link.slug}
                    onChange={(e) =>
                      updateLink(colIdx, linkIdx, "slug", e.target.value)
                    }
                  />
                  <button
                    onClick={() => removeLink(colIdx, linkIdx)}
                    style={S.iconBtn(C.red)}
                  >
                    <FiX size={12} />
                  </button>
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <input
                    style={{
                      flex: 2,
                      padding: "8px 12px",
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      fontSize: 13,
                    }}
                    placeholder="Image URL (for hover preview)"
                    value={link.image || ""}
                    onChange={(e) =>
                      updateLink(colIdx, linkIdx, "image", e.target.value)
                    }
                  />
                  {link.image && (
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: 8,
                        overflow: "hidden",
                        background: "#e2e8f0",
                      }}
                    >
                      <img
                        src={link.image}
                        alt={link.label}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                        onError={(e) => (e.target.style.display = "none")}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button
              onClick={() => addLink(colIdx)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                background: "transparent",
                border: `1px dashed ${C.primary}`,
                borderRadius: 8,
                color: C.primary,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              <FiPlus size={12} /> Add Link
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Simple Sub Links Editor Component ────────────────────────────────────────
function SimpleSubLinksEditor({ value, onChange }) {
  const [links, setLinks] = useState(value || []);

  const addLink = () => {
    const updated = [...links, { label: "", slug: "", image: "" }];
    setLinks(updated);
    onChange(updated);
  };

  const updateLink = (idx, field, val) => {
    const updated = [...links];
    updated[idx][field] = val;
    setLinks(updated);
    onChange(updated);
  };

  const removeLink = (idx) => {
    const updated = links.filter((_, i) => i !== idx);
    setLinks(updated);
    onChange(updated);
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <label
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: C.textLight,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Sub-menu Links
        </label>
        <button
          onClick={addLink}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: C.primaryLight,
            border: "none",
            borderRadius: 8,
            color: C.white,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          <FiPlus size={12} /> Add Link
        </button>
      </div>

      {links.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: 32,
            background: "#f8fafc",
            borderRadius: 12,
            color: C.textLight,
          }}
        >
          <FiLink size={32} style={{ opacity: 0.5, marginBottom: 8 }} />
          <p>No sub-links configured</p>
        </div>
      )}

      {links.map((link, idx) => (
        <div
          key={idx}
          style={{
            marginBottom: 16,
            padding: 16,
            background: "#f8fafc",
            borderRadius: 12,
          }}
        >
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <input
              style={{
                flex: 1,
                padding: "8px 12px",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: 13,
              }}
              placeholder="Label"
              value={link.label}
              onChange={(e) => updateLink(idx, "label", e.target.value)}
            />
            <input
              style={{
                flex: 1,
                padding: "8px 12px",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: 13,
              }}
              placeholder="Slug"
              value={link.slug}
              onChange={(e) => updateLink(idx, "slug", e.target.value)}
            />
            <button onClick={() => removeLink(idx)} style={S.iconBtn(C.red)}>
              <FiTrash2 size={12} />
            </button>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <input
              style={{
                flex: 2,
                padding: "8px 12px",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: 13,
              }}
              placeholder="Image URL (optional)"
              value={link.image || ""}
              onChange={(e) => updateLink(idx, "image", e.target.value)}
            />
            {link.image && (
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#e2e8f0",
                }}
              >
                <img
                  src={link.image}
                  alt={link.label}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Category Modal ───────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: "",
  slug: "",
  description: "",
  image: "",
  bannerImage: "",
  displayOrder: 0,
  metaTitle: "",
  metaDescription: "",
  isActive: true,
  isMegaMenu: false,
  megaMenuColumns: [],
  simpleSubLinks: [],
};

function slugify(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function CategoryModal({ category, onClose, onSaved, toast }) {
  const isEdit = !!category?.id;
  const [form, setForm] = useState(() => {
    if (isEdit) return JSON.parse(JSON.stringify(category));
    return { ...EMPTY_FORM };
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function setField(key, val) {
    setForm((f) => {
      const updated = { ...f, [key]: val };
      if (key === "name" && !isEdit) updated.slug = slugify(val);
      return updated;
    });
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast("Category name is required", "error");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await catPut(category.id, form);
        toast("Category updated successfully");
      } else {
        await catPost(form);
        toast("Category created successfully");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast(e.message || "Failed to save category", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await catDelete(category.id);
      toast("Category deleted successfully");
      onSaved();
      onClose();
    } catch (e) {
      toast(e.message || "Failed to delete category", "error");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: C.surface,
          borderRadius: 20,
          width: "100%",
          maxWidth: 900,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: C.shadowLg,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <h3
            style={{
              fontSize: 18,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <MdCategory style={{ color: C.primary }} />
            {isEdit ? "Edit Category" : "Create New Category"}
          </h3>
          <button onClick={onClose} style={S.iconBtn(C.textLight)}>
            <FiX size={18} />
          </button>
        </div>

        <div style={{ padding: "24px" }}>
          {/* Basic Info Row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 16,
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textLight,
                  marginBottom: 6,
                }}
              >
                Category Name *
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontSize: 13,
                }}
                value={form.name || ""}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g., Lungies"
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textLight,
                  marginBottom: 6,
                }}
              >
                Slug
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontSize: 13,
                }}
                value={form.slug || ""}
                onChange={(e) => setField("slug", slugify(e.target.value))}
                placeholder="auto-generated"
              />
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: C.textLight,
                marginBottom: 6,
              }}
            >
              Description
            </label>
            <textarea
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: 13,
                minHeight: 80,
                resize: "vertical",
              }}
              value={form.description || ""}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="Category description..."
            />
          </div>

          {/* Images */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textLight,
                  marginBottom: 6,
                }}
              >
                Card Image URL
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontSize: 13,
                }}
                value={form.image || ""}
                onChange={(e) => setField("image", e.target.value)}
                placeholder="https://..."
              />
              {form.image && (
                <img
                  src={form.image}
                  alt="preview"
                  style={{
                    width: "100%",
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 8,
                    marginTop: 8,
                  }}
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textLight,
                  marginBottom: 6,
                }}
              >
                Banner Image URL
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontSize: 13,
                }}
                value={form.bannerImage || ""}
                onChange={(e) => setField("bannerImage", e.target.value)}
                placeholder="https://..."
              />
              {form.bannerImage && (
                <img
                  src={form.bannerImage}
                  alt="preview"
                  style={{
                    width: "100%",
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 8,
                    marginTop: 8,
                  }}
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </div>
          </div>

          {/* Display Order & Active */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textLight,
                  marginBottom: 6,
                }}
              >
                Display Order
              </label>
              <input
                type="number"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  fontSize: 13,
                }}
                value={form.displayOrder || 0}
                onChange={(e) =>
                  setField("displayOrder", parseInt(e.target.value) || 0)
                }
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                background: "#f8fafc",
                borderRadius: 8,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 500 }}>Active</span>
              <button
                onClick={() => setField("isActive", !form.isActive)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 24,
                  color: form.isActive ? C.green : C.textLight,
                  cursor: "pointer",
                }}
              >
                {form.isActive ? <FiToggleRight /> : <FiToggleLeft />}
              </button>
            </div>
          </div>

          {/* Mega Menu Toggle */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 0",
              borderTop: `1px solid ${C.border}`,
              marginBottom: 20,
            }}
          >
            <div>
              <span style={{ fontSize: 14, fontWeight: 600 }}>
                Enable Mega Menu
              </span>
              <p style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>
                Display multi-column dropdown with images
              </p>
            </div>
            <button
              onClick={() => setField("isMegaMenu", !form.isMegaMenu)}
              style={{
                background: "none",
                border: "none",
                fontSize: 28,
                color: form.isMegaMenu ? C.primary : C.textLight,
                cursor: "pointer",
              }}
            >
              {form.isMegaMenu ? <FiToggleRight /> : <FiToggleLeft />}
            </button>
          </div>

          {/* Conditional Editors */}
          {form.isMegaMenu ? (
            <MegaMenuEditor
              value={form.megaMenuColumns || []}
              onChange={(val) => setField("megaMenuColumns", val)}
            />
          ) : (
            <SimpleSubLinksEditor
              value={form.simpleSubLinks || []}
              onChange={(val) => setField("simpleSubLinks", val)}
            />
          )}

          {/* SEO */}
          <div style={{ marginBottom: 24 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: C.textLight,
                marginBottom: 6,
              }}
            >
              Meta Title
            </label>
            <input
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: 13,
                marginBottom: 12,
              }}
              value={form.metaTitle || ""}
              onChange={(e) => setField("metaTitle", e.target.value)}
              placeholder="SEO title..."
            />
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: C.textLight,
                marginBottom: 6,
              }}
            >
              Meta Description
            </label>
            <textarea
              style={{
                width: "100%",
                padding: "10px 12px",
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                fontSize: 13,
                minHeight: 60,
                resize: "vertical",
              }}
              value={form.metaDescription || ""}
              onChange={(e) => setField("metaDescription", e.target.value)}
              placeholder="SEO description..."
            />
          </div>

          {/* Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              paddingTop: 20,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            {isEdit && !confirmDelete && (
              <button
                onClick={() => setConfirmDelete(true)}
                style={{
                  padding: "10px 20px",
                  background: "rgba(239,68,68,0.1)",
                  border: `1px solid ${C.red}`,
                  borderRadius: 10,
                  color: C.red,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <FiTrash2 /> Delete
              </button>
            )}
            {confirmDelete && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: "10px 20px",
                  background: C.red,
                  border: "none",
                  borderRadius: 10,
                  color: C.white,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {deleting ? (
                  <FiLoader style={{ animation: "spin 1s linear infinite" }} />
                ) : (
                  <FiTrash2 />
                )}{" "}
                Confirm Delete
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: "10px 20px",
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: "10px 24px",
                background: C.primaryGradient,
                border: "none",
                borderRadius: 10,
                color: C.white,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {saving ? (
                <FiLoader style={{ animation: "spin 1s linear infinite" }} />
              ) : (
                <FiCheck />
              )}
              {isEdit ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Category Card ─────────────────────────────────────────────────────────────
function CategoryCard({ cat, onEdit, viewMode }) {
  const isGridView = viewMode === "grid";
  return (
    <div style={{ ...S.card, cursor: "pointer" }} onClick={() => onEdit(cat)}>
      {isGridView ? (
        <>
          {cat.image ? (
            <img
              src={cat.image}
              alt={cat.name}
              style={S.cardImage}
              onError={(e) => (e.target.style.display = "none")}
            />
          ) : (
            <div style={S.cardImagePlaceholder}>
              <FiImage size={48} />
            </div>
          )}
          <div style={S.cardBody}>
            <div style={S.cardHeader}>
              <h3 style={S.cardName}>{cat.name}</h3>
            </div>
            <span style={S.cardSlug}>/{cat.slug}</span>
            {cat.description && <p style={S.cardDesc}>{cat.description}</p>}
            <div style={S.cardMeta}>
              <span style={S.badge(cat.isActive)}>
                {cat.isActive ? <FiEye size={10} /> : <FiEyeOff size={10} />}{" "}
                {cat.isActive ? "Active" : "Inactive"}
              </span>
              {cat.isMegaMenu && <span style={S.megaBadge}>Mega Menu</span>}
              <span style={S.orderBadge}>
                <FiMove size={10} /> Order: {cat.displayOrder}
              </span>
            </div>
          </div>
        </>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: 12,
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 10,
              overflow: "hidden",
              background: "#f1f5f9",
              flexShrink: 0,
            }}
          >
            {cat.image ? (
              <img
                src={cat.image}
                alt={cat.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiImage />
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 4,
              }}
            >
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
                {cat.name}
              </h3>
              <span style={S.badge(cat.isActive)}>
                {cat.isActive ? "Active" : "Inactive"}
              </span>
              {cat.isMegaMenu && <span style={S.megaBadge}>Mega Menu</span>}
            </div>
            <div
              style={{
                display: "flex",
                gap: 16,
                fontSize: 12,
                color: C.textLight,
              }}
            >
              <span>Slug: /{cat.slug}</span>
              <span>Order: {cat.displayOrder}</span>
              {cat.simpleSubLinks && cat.simpleSubLinks.length > 0 && (
                <span>{cat.simpleSubLinks.length} sub-links</span>
              )}
              {cat.megaMenuColumns && cat.megaMenuColumns.length > 0 && (
                <span>{cat.megaMenuColumns.length} columns</span>
              )}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(cat);
            }}
            style={S.iconBtn(C.primary)}
          >
            <FiEdit2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Categories Component ─────────────────────────────────────────────────
export default function Categories({ toast: parentToast }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalCat, setModalCat] = useState(null);  // null = closed, {} = create, {id,...} = edit
  const [viewMode, setViewMode] = useState("grid");

  const toast = useCallback(
    (msg, type = "success") => {
      if (parentToast) parentToast(msg, type);
      else console.log(`${type}: ${msg}`);
    },
    [parentToast],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await catGet();
      const sorted = (data.categories || []).sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
      );
      setCategories(sorted);
    } catch (e) {
      toast(e.message, "error");
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = categories.filter(
    (c) =>
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      c.slug?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.titleSection}>
          <div>
            <p style={S.subtitle}>
              Organize your products with categories and mega menus
            </p>
          </div>
        </div>
        <div style={S.stats}>
          <div style={S.statBadge}>
            <FiFolder size={14} /> {categories.length} Categories
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <FiSearch style={S.searchIcon} />
          <input
            style={S.searchInput}
            placeholder="Search categories by name or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: C.textLight,
              }}
            >
              <FiX size={14} />
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <div style={S.viewToggle}>
            <button
              onClick={() => setViewMode("grid")}
              style={S.viewBtn(viewMode === "grid")}
            >
              <FiGrid /> Grid
            </button>
            <button
              onClick={() => setViewMode("list")}
              style={S.viewBtn(viewMode === "list")}
            >
              <FiList /> List
            </button>
          </div>
          {/* "New Category" button: open modal with empty object (no id = create mode) */}
          <button style={S.addBtn} onClick={() => setModalCat({})}>
            <FiPlus /> New Category
          </button>
        </div>
      </div>

      {/* Result count */}
      {!loading && (
        <div style={{ marginBottom: 20, fontSize: 13, color: C.textLight }}>
          {filtered.length} {filtered.length === 1 ? "category" : "categories"}{" "}
          found
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={S.loadingState}>
          <FiLoader
            size={32}
            style={{ animation: "spin 1s linear infinite" }}
          />
        </div>
      )}

      {/* Empty State */}
      {!loading && filtered.length === 0 && (
        <div style={S.emptyState}>
          <MdCategory size={64} style={{ opacity: 0.5, marginBottom: 16 }} />
          <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
            No categories found
          </p>
          <p style={{ fontSize: 13, color: C.textLight, marginBottom: 20 }}>
            {search
              ? "Try a different search term"
              : "Get started by creating your first category"}
          </p>
          {!search && (
            <button
              onClick={() => setModalCat({})}
              style={{
                padding: "10px 20px",
                background: C.primaryGradient,
                border: "none",
                borderRadius: 10,
                color: C.white,
                cursor: "pointer",
              }}
            >
              <FiPlus /> Create Category
            </button>
          )}
        </div>
      )}

      {/* Categories Grid/List */}
      {!loading && filtered.length > 0 && (
        <div style={viewMode === "grid" ? S.grid : S.list}>
          {filtered.map((cat) => (
            <CategoryCard
              key={cat.id}
              cat={cat}
              onEdit={setModalCat}  
              viewMode={viewMode}
            />
          ))}
        </div>
      )}

      {/* Modal — key fix here:
           modalCat = {}        → no id → category prop = undefined → CREATE mode
           modalCat = {id,...} → has id → category prop = full object → EDIT mode
      */}
      {modalCat !== null && (
        <CategoryModal
          category={modalCat.id ? modalCat : undefined}
          onClose={() => setModalCat(null)}
          onSaved={load}
          toast={toast}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}